import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCreateWorkspaceForm } from '@/pages/CreateWorkspace/useCreateWorkspaceForm'
import { createWorkspace } from '@/services/workspaces/workspaceRepository'
import {
  assertWorkspaceCap,
  assertWorkspaceNameAvailable,
} from '@/services/workspaces/workspaceValidation'
import { useWorkspaceStore } from '@/store/workspaceStore'
import { workspacesQueryKey } from '@/hooks/useWorkspaces'
import { useWorkspaceSwitcher } from '@/components/PrimaryLeftNavigation/useWorkspaceSwitcher'
import { appendWorkspaceCache } from '@/services/workspaces/workspaceQueryKeys'
import { applyCreateWorkspaceValidation } from '@/hooks/applyCreateWorkspaceValidation'

export function useCreateWorkspace({ onCreated, switchOnSuccess = true } = {}) {
  const form = useCreateWorkspaceForm()
  const queryClient = useQueryClient()
  const switchWorkspace = useWorkspaceSwitcher()
  const setActiveWorkspaceId = useWorkspaceStore((s) => s.setActiveWorkspaceId)
  const submit = useMutation({
    mutationFn: (payload) => createWorkspace(payload),
    onSuccess: (workspace) => {
      setActiveWorkspaceId(workspace.id)
      appendWorkspaceCache(queryClient, workspace)
      if (switchOnSuccess) switchWorkspace(workspace.id)
      onCreated?.(workspace)
    },
    onError: (error) => applyCreateWorkspaceValidation(error, form),
  })

  const onSubmit = (event) => {
    event.preventDefault()
    if (!form.canSubmit || submit.isPending) return
    form.clearErrors()
    const workspaces = queryClient.getQueryData(workspacesQueryKey)?.workspaces ?? []
    try {
      assertWorkspaceCap(workspaces)
      assertWorkspaceNameAvailable(workspaces, form.payload.name)
    } catch (error) {
      applyCreateWorkspaceValidation(error, form)
      return
    }
    submit.mutate(form.payload)
  }

  return { form, submit, onSubmit }
}
